import { datadogLogs } from '@datadog/browser-logs';
import {
  colors,
  ImpButton,
  ImpContainer,
  ImpFlex,
  ImpInput,
  ImpPad,
  ImpSpacer,
  ImpText,
  spacing,
} from '@imprint-payments/imprint-ui';
import React, { useEffect, useState } from 'react';

import { EVENT } from '../../analytics/analyticsConsts';
import useCheckoutAndLogin from '../../api/login/useCheckoutAndLogin';
import { MODAL_ENUM } from '../../app/applicationScreenSlice';
import { useAppSelector, useImpState } from '../../app/hooks';
import {
  resetInputValidation,
  setPhoneNumberError,
  setSsnLast4Error,
} from '../../app/validatorSlice';
import { PHONE_NUMBER_MAX_LENGTH } from '../../assets/consts/const';
import { ANALYTICS } from '../../firebase/firebase';
import {
  trackPostieEvent,
  usePostieTrackingInit,
} from '../../hooks/usePostieTrackingPixel';
import { useStytchTelemetryInit } from '../../hooks/useStytchTelemetry';
import { formatPhoneNumber } from '../../utils/formatUtils';
import { handleFormSubmit } from '../../utils/functionUtils';
import {
  getValidationString,
  hideWhileTyping,
  noError,
  validateLastFourSSN,
  validatePhoneNumber,
  ValidationResponse,
} from '../../utils/validateUtils';
import InStoreModal from '../application/InStoreModal';
import LoginModal from '../loginModal/LoginModal';
import { LanguageSelection } from '../personalinfo/LanguageSelection';
import { useProveContext } from './ProveContext';

export default function LetsGetStarted() {
  const { language, dispatch, appScreenState } = useImpState();
  const { optInPrefill } = useProveContext();
  const validator: any = useAppSelector((state) => state.validatorSelector);

  const [phone, setPhone] = useState('');
  const [ssn4, setSsn4] = useState('');

  const checkoutAndLogin = useCheckoutAndLogin();
  usePostieTrackingInit();
  useStytchTelemetryInit();

  useEffect(() => {
    datadogLogs.logger.info(EVENT.LETS_GET_STARTED_VIEWED);
    ANALYTICS.logEvent(EVENT.LETS_GET_STARTED_VIEWED);
    trackPostieEvent('trackPageView');
  }, []);

  const renderExistingUserModal = () => {
    switch (appScreenState.showModal) {
      case MODAL_ENUM.showLoginModal:
        return (
          <LoginModal
            onClose={() => {
              setPhone('');
              setSsn4('');
            }}
          />
        );
      case MODAL_ENUM.showWelcomeBackInStoreModal:
        return <InStoreModal sourcePage="existing" />;
      default:
        return null;
    }
  };

  const continueClick = () => {
    dispatch(resetInputValidation());
    checkoutAndLogin.mutate({ phone, ssn4 });
  };

  //TODO: need handle submitting state
  const buttonState =
    validator.phoneNumberError === ValidationResponse.valid &&
    validator.lastFourError === ValidationResponse.valid
      ? 'enabled'
      : 'disabled';

  return (
    <form onSubmit={handleFormSubmit}>
      {renderExistingUserModal()}
      <ImpFlex direction={'column'}>
        <ImpSpacer height="xl" />
        <ImpContainer textAlign={'center'}>
          <ImpFlex direction={'column'} gap={spacing.xs} align={'center'}>
            <ImpText typography={'headline3'}>
              {language.letsGetStarted}
            </ImpText>

            <ImpText
              typography={'body2'}
              color={colors.content.onBackgroundVariant}
            >
              {language.getStartedSubtitle}
            </ImpText>
          </ImpFlex>
        </ImpContainer>
        <ImpSpacer height={spacing.xl} />

        <ImpFlex
          direction={'column'}
          gap={spacing.m}
          dataTestId={'input-container'}
          align={'normal'}
        >
          <LanguageSelection />
          <PhoneInput phone={phone} setPhone={setPhone} />
          <SSN4Field lastFour={ssn4} setLastFour={setSsn4} />
        </ImpFlex>

        <ImpPad padding={spacing.l} fluid>
          <ImpFlex direction={'column'} align={'center'} gap="8px">
            <ImpButton
              onClick={() => {
                optInPrefill.current = true;
                continueClick();
                ANALYTICS.logEvent(EVENT.LETS_GET_STARTED_CONTINUE_CLICKED);
              }}
              buttonStyle={'branded'}
              state={buttonState}
              size="fill"
              type="submit"
            >
              {language.continueBtn}
            </ImpButton>
            <ImpPad padding="14px 0px">
              <ImpButton
                variant={'text'}
                buttonStyle={'branded'}
                state={buttonState}
                onClick={() => {
                  optInPrefill.current = false;
                  continueClick();
                  ANALYTICS.logEvent(EVENT.ENTER_INFO_MANUALLY_CLICKED);
                }}
                type="button"
              >
                {language.enterInfoManually}
              </ImpButton>
            </ImpPad>
          </ImpFlex>
        </ImpPad>
      </ImpFlex>
    </form>
  );
}

function PhoneInput({
  phone,
  setPhone,
}: {
  phone: string;
  setPhone: React.Dispatch<string>;
}) {
  const { language, dispatch } = useImpState();
  const validator = useAppSelector((state) => state.validatorSelector);

  useEffect(() => {
    if (validator.phoneNumberError === ValidationResponse.valid) {
      ANALYTICS.logEvent(EVENT.MOBILE_PHONE_NUMBER_FILLED_OUT);
    }
  }, [validator.phoneNumberError]);

  useEffect(() => {
    const validationResponse = validatePhoneNumber(phone);
    if (
      phone.length < PHONE_NUMBER_MAX_LENGTH &&
      hideWhileTyping.includes(validationResponse)
    ) {
      dispatch(setPhoneNumberError(ValidationResponse.empty));
    } else {
      dispatch(setPhoneNumberError(validationResponse));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  return (
    <ImpInput
      label={language.phoneNumber}
      value={phone}
      showError={!noError.includes(validator.phoneNumberError)}
      errorMessage={getValidationString(validator.phoneNumberError, language)}
      maxLength={PHONE_NUMBER_MAX_LENGTH}
      onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
      onBlur={() => {
        dispatch(setPhoneNumberError(validatePhoneNumber(phone)));
      }}
      caption={language.personalInfoNumberInstruction}
      numeric
      autocomplete="tel-national"
      autofocus
      maxWidth="100%"
    />
  );
}

// extracted from IncomeHousingV2, very messy
export function SSN4Field({
  lastFour,
  setLastFour,
}: {
  lastFour: string;
  setLastFour: (newSSN: string) => void;
}) {
  const { appScreenState, language, dispatch, appStatus } = useImpState();
  const validator: any = useAppSelector((state) => state.validatorSelector);

  useEffect(() => {
    const validationResponse = validateLastFourSSN(lastFour);
    if (!hideWhileTyping.includes(validationResponse)) {
      dispatch(setSsnLast4Error(validationResponse));
    } else {
      dispatch(setSsnLast4Error(ValidationResponse.empty));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastFour]);

  return (
    <ImpContainer>
      <ImpInput
        label={language.lastFour}
        value={lastFour}
        maxLength={4}
        id="ssn"
        autocomplete="ssn"
        numeric
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const numStrings = '0123456789'.split('');
          const filteredValue = e.target.value
            .split('')
            .filter((char) => numStrings.includes(char))
            .join('');

          setLastFour(filteredValue);
        }}
        showError={!noError.includes(validator.lastFourError)}
        errorMessage={getValidationString(validator.lastFourError, language)}
        instrumentation={() => {
          ANALYTICS.logEvent(EVENT.SSN4_FILLED_OUT);
        }}
        onBlur={() => {
          dispatch(setSsnLast4Error(validateLastFourSSN(lastFour)));
        }}
        maxWidth="100%"
        isDisabled={
          appScreenState.requireFullSSN || appStatus.reApplicationInApp
        }
        caption={language.ssnDisclosure}
      />
    </ImpContainer>
  );
}
